import angular from 'angular';
import booksServices from '../../booksServices';
import booksMenu from '../../menu/Menu';
import CSMCommonWeb from 'csm-common-web';
import makeDepositTemplate from './MakeDeposit.html';
/** SCSS */
import './MakeDeposit.scss';
import './dom/dom.scss';
/** Services */
import { DepositsServiceProvider } from './services/DepositsService';
import { runDepositInfoData } from './services/Data/DepositInfo';
import { runDepositReceiptItemData } from './services/Data/DepositReceiptItem';
import { runDepositReceiptPaymentItemData } from './services/Data/DepositReceiptPaymentItem';
import { runDepositReceiptSalesItemData } from './services/Data/DepositReceiptSalesItem';
import { runQuickDepositItemData } from './services/Data/QuickDepositItem';
/** Dom */
import { amountRangeComponent } from './dom/amountRange';
import { dateRangeComponent } from './dom/dateRange';
import { findDialogDirective } from './dom/findDialog';
import { itemListComponent, listColumnDirective } from './dom/itemList';
import { paymentDetailsToggleComponent } from './dom/paymentDetailsToggle';
import { quickItemDialogDirective } from './dom/quickItemDialog';
import { receiptsDialogDirective } from './dom/receiptsDialog';

/*******************************************************************************
 * @ngdoc overview
 * @name  makeDeposit
 *
 * @description
 * Create/Review Deposit Slips.
 *
 * Functionalities to keep in mind:
 * - When opening the list of Undeposited Receipts, the list of receipts will be
 *   filtered by the date of the deposit.
 * - If receipts have been added to the deposit, and then the date is changed,
 *   any receipts that were created after this new date will be removed from the
 *   deposit.
 * - When reviewing a deposit, if there are any QuickDepositItems, the account
 *   and memo can be edited, as long as the deposit has not been cleared. They
 *   cannot be removed from the deposit, or have the amount changed.
 *******************************************************************************/
export default angular
  .module('csm.books.MakeDeposit', [
    booksServices,
    booksMenu,
    CSMCommonWeb,
    'csm.util.tools'
  ])
  .factory('Globals', intiGlobalFactory)

  .controller('mainController', MainController)

  /*******************************************************************************
   * @ngdoc    component
   * @name     csm.books.MakeDeposit.component:makeDeposit
   * @description
   *
   *******************************************************************************/
  .component('makeDeposit', {
    template: makeDepositTemplate,
    controller: controller,
    transclude: {
      depositAdjAcct: '?depositAdjAcct'
    }
  })
  .provider('DepositsService', DepositsServiceProvider)
  .factory('Data', initDataFactory)
  .run(runDepositInfoData)
  .run(runDepositReceiptItemData)
  .run(runDepositReceiptPaymentItemData)
  .run(runDepositReceiptSalesItemData)
  .run(runQuickDepositItemData)
  .component('amountRange', amountRangeComponent)
  .component('dateRange', dateRangeComponent)
  .directive('findDialog', findDialogDirective)
  .component('itemList', itemListComponent)
  .directive('listColumn', listColumnDirective)
  .component('paymentDetailsToggle', paymentDetailsToggleComponent)
  .directive('quickItemDialog', quickItemDialogDirective)
  .directive('receiptsDialog', receiptsDialogDirective).name;

initDataFactory.$inject = ['SERVER'];
intiGlobalFactory.$inject = [];

/*******************************************************************************
 * @ngdoc    service
 * @name     csm.books.MakeDeposit.service:Global
 * @property {Object} organization  Current organization info.
 * @property {Object} accounts      Lists of Bank, Expense, and Revenue accounts.
 * @property {Object} defaults      Default properties.
 * @description
 * Global values. Set by controller on initialization.
 *******************************************************************************/
/* @ngInject */ function intiGlobalFactory() {
  return {};
}

/*******************************************************************************
 * @ngdoc    controller
 * @name     csm.books.MakeDeposit.controller:mainController
 * @description
 * Loads root scope and sets the appFlags.
 *******************************************************************************/
/* @ngInject */ function MainController($rootScope, $appFlags) {
  $rootScope.$appFlags = $appFlags;
}

/* @ngInject */ function initDataFactory(SERVER) {
  return {};
}

/*******************************************************************************
 * controller */
/**
 * @ngdoc controller
 * @name  csm.books.MakeDeposit.makeDeposit.controller:controller
 * @description
 * Handles the main workflow of a Deposit. On first load, the default properties
 * are set, and the Undeposited Receipts dialog is opened. A user may select
 * receipts from this dialog to add to the current deposit.
 *
 * Once receipts are on the Deposit, the user would then enter in the actual
 * checks and cash being deposited. If there is a discrepency, the user should
 * put the remaining amount in a single adjustment account.
 *
 * For PTAEZ accounts, the user will have the option of adding deposit items that
 * were not from any receipt. This can be added as cash or check, and an account
 * must be selected to go with that Quick Deposit.
 *******************************************************************************/
/* @ngInject */ function controller(
  $injector,
  $scope,
  $q,
  $timeout,
  BooksServices,
  BusyScreen,
  Data,
  Globals,
  DepositsService,
  SERVER,
  CompilerService,
  $element,
  $transclude
) {
  var $ctrl = this;
  $ctrl.validator = {};

  /*------------------------------------------------------------------*/
  /* Insert transcluded content and add reference to this controller. */
  /*------------------------------------------------------------------*/
  CompilerService.transcludeSlots($element, { $ctrl: this }, $transclude);

  /** Deposit Watcher. */
  var mDepositWatcher = {
    /*---------------------------------------------------------------*/
    /* Initialize the `deregister` function to do nothing. This will */
    /* be overridden when `register` is called for the first time.   */
    /*---------------------------------------------------------------*/
    deregister: angular.noop,
    register: function () {
      /*---------------------------------------------------*/
      /* Add the modified property as un-enumerable so the */
      /* watch will ignore it in its checks for equality.  */
      /*---------------------------------------------------*/
      Object.defineProperty($ctrl.depositInfo, 'modified', {
        enumberable: false,
        writable: true,
        value: false
      });

      this.deregister = $scope.$watch(
        function () {
          return $ctrl.depositInfo;
        },
        function () {
          $ctrl.setModified(true);
        },
        true
      );

      /*---------------------------------------------------------*/
      /* Clear the modified property after the next digest       */
      /* cycle as it was set during the initialization of watch. */
      /*---------------------------------------------------------*/
      $timeout($ctrl.setModified);
    }
  };

  $q.all({
    orgInfo: BooksServices.Session.getOrganization(),
    accounts: DepositsService.getAccountLists(),
    defaults: DepositsService.defaults()
  }).then(function (promises) {
    Globals.organization = promises.orgInfo;
    Globals.accounts = promises.accounts;
    Globals.defaults = promises.defaults;

    /*------------------------------*/
    /* Set "first load" properties. */
    /*------------------------------*/
    $ctrl.dpBankAccounts = Globals.accounts.bank;
    $ctrl.dpExpenseAccounts = Globals.accounts.expense;
    $ctrl.dpAdjustmentAccounts = Globals.accounts.adjustments;
    $ctrl.useQD = SERVER.BooksWebConstants.allowQuickDepositItems;
    $ctrl.handwrittenLabel = SERVER.BooksWebConstants.handwrittenLabel;

    /*------------------------------*/
    /* Set functions on controller. */
    /*------------------------------*/
    $ctrl.addReceipts = $injector.invoke(addReceipts, null, {
      $ctrl: $ctrl,
      $scope: $scope
    });
    $ctrl.checkModifications = $injector.invoke(checkModifications, null, {
      $ctrl: $ctrl,
      $scope: $scope
    });
    $ctrl.clear = $injector.invoke(clear, null, {
      $ctrl: $ctrl,
      $scope: $scope
    });
    $ctrl.drop = $injector.invoke(drop, null, {
      $ctrl: $ctrl,
      $scope: $scope
    });
    $ctrl.handwritten = $injector.invoke(handwritten, null, {
      $ctrl: $ctrl,
      $scope: $scope
    });
    $ctrl.loadDeposit = $injector.invoke(loadDeposit, null, {
      $ctrl: $ctrl,
      $scope: $scope,
      Watch: mDepositWatcher
    });
    $ctrl.print = $injector.invoke(print, null, {
      $ctrl: $ctrl,
      $scope: $scope
    });
    $ctrl.saveLabel = $injector.invoke(saveLabel, null, {
      $ctrl: $ctrl,
      $scope: $scope
    });
    $ctrl.save = $injector.invoke(save, null, {
      $ctrl: $ctrl,
      $scope: $scope
    });
    $ctrl.setModified = $injector.invoke(setModified, null, {
      $ctrl: $ctrl,
      $scope: $scope,
      Watch: mDepositWatcher
    });
    $ctrl.updateActuals = $injector.invoke(updateActuals, null, {
      $ctrl: $ctrl,
      $scope: $scope
    });
    $ctrl.updateTotals = $injector.invoke(updateTotals, null, {
      $ctrl: $ctrl,
      $scope: $scope
    });
    $ctrl.validate = $injector.invoke(validate, null, {
      $ctrl: $ctrl,
      $scope: $scope
    });
    $ctrl.viewDeposit = $injector.invoke(viewDeposit, null, {
      $ctrl: $ctrl,
      $scope: $scope
    });

    /*-------------------------------------------------------*/
    /* Clear the depositInfo to be ready for input. We need  */
    /* to wait until the depositInfo object has been created */
    /* before creating anything that works with that object. */
    /*-------------------------------------------------------*/
    $ctrl.clear();
    $ctrl.allowEdit = function () {
      return $ctrl.depositInfo.isNew;
    };

    /*---------------------------------------*/
    /* If the entry date changes, remove any */
    /* receipts that are no longer valid.    */
    /*---------------------------------------*/
    $scope.$watch(
      function () {
        return $ctrl.depositInfo.entryDate;
      },
      function () {
        /*------------------------------------------------*/
        /* Prevent any changes if the deposit is not new. */
        /*------------------------------------------------*/
        if (!$ctrl.depositInfo.isNew) return;

        /*----------------------------------------------------*/
        /* Create a new date object where the time is set to  */
        /* the last second of the day in the original `date`. */
        /*----------------------------------------------------*/
        function endDate(date) {
          if (!date) return;

          var year = date.getFullYear();
          var month = date.getMonth();
          var day = date.getDate();

          /*------------------------------------------*/
          /* Go to the next day, and remove a second. */
          /*------------------------------------------*/
          return new Date(new Date(year, month, day + 1) - 1000);
        }

        /*------------------------------------*/
        /* Remove any receipt items that were */
        /* created after the Deposit Date.    */
        /*------------------------------------*/
        if ($ctrl.depositInfo.entryDate) {
          var depositDate = endDate($ctrl.depositInfo.entryDate);
          $ctrl.depositInfo.items = $ctrl.depositInfo.items.filter(
            function (item) {
              return item.isQuickItem || item.receiptDate <= depositDate;
            }
          );
        }

        $ctrl.updateActuals();
      }
    );

    /*------------------------------------*/
    /* Open the Undeposited Funds dialog. */
    /*------------------------------------*/
    document.querySelector('[receipts-dialog]').click();
    // We do not set busy to false here we let the receipts dialog manage
    // that so that way the user does not interact before that query finishes
  });
}

/*******************************************************************************
 * controller.addReceipts */
/**
 * @ngdoc    function
 * @name     addReceipts
 * @methodOf csm.books.MakeDeposit.controller:controller
 * @param    {Array} $receipts  List of receipts to add to deposit.
 * @description
 * Receipts dialog handler for adding receipts to the deposit. Clears the totals
 * and reloads the receipts to the deposit receipts list.
 *******************************************************************************/
/* @ngInject */ function addReceipts($ctrl, $q) {
  return function ($items, $totals) {
    $ctrl.depositInfo.items = $items.concat($ctrl.depositInfo.items);
    $ctrl.depositInfo.totalNumberChecks = $totals.checksCount;
    $ctrl.depositInfo.totalAmountChecks = $totals.totalChecks;
    $ctrl.depositInfo.totalAmountCash = $totals.totalCash;
    $ctrl.updateTotals();

    return $q.resolve();
  };
}

/*******************************************************************************
 * $ctrl.checkModifications */
/**
 * @ngdoc    function
 * @name     checkModifications
 * @methodOf csm.books.MakeDeposit.controller:controller
 * @param    {Function}  Method to call unless there are modifications that don't
 *                       want to be lost.
 * @description
 * Checks the `depositInfo` for a change to the `modified` flag. If there are
 * changes, then the user will be asked to confirm moving away from the current
 * deposit, thus losing those changes.
 * If there were no changes, or the user agrees to lose any changes that were
 * made, the supplied `method` will be called.
 *******************************************************************************/
/* @ngInject */ function checkModifications($ctrl, Alert) {
  return function (method) {
    /*------------------------------------------------------*/
    /* If there are modifications, check with the user      */
    /* before trying to move away from the current deposit. */
    /*------------------------------------------------------*/
    if ($ctrl.depositInfo.modified) {
      var title = $ctrl.depositInfo.isNew
        ? 'Cancel New Deposit'
        : 'Cancel Changes';
      var msg = $ctrl.depositInfo.isNew
        ? 'Are you sure you want to cancel this Deposit?'
        : 'You have made changes to this deposit. Are you sure you want to cancel these changes?';

      Alert.confirm(msg, title).then(function (result) {
        /*--------------------------------------------*/
        /* User agrees to continue, and lose changes. */
        /*--------------------------------------------*/
        method();
      });
    } else method();

    /*-------------------------------------------------*/
    /* There were no changes made, continue as normal. */
    /*-------------------------------------------------*/
  };
}

/*******************************************************************************
 * controller.clear */
/**
 * @ngdoc    method
 * @name     clear
 * @methodof csm.books.MakeDeposit.controller:controller
 * @description
 * Creates a new deposit object and sets default properties.
 *******************************************************************************/
/* @ngInject */ function clear(
  $ctrl,
  Data,
  Globals,
  LocalDateSerializer,
  SERVER
) {
  return function () {
    var deposit = new Data.DepositInfo();
    deposit.handwritten = SERVER.BooksWebConstants.defaultHandwritten;
    deposit.items = [];
    deposit.entryDate = LocalDateSerializer.deserialize(
      Globals.defaults.entryDate
    );
    deposit.memo = Globals.defaults.memo;
    deposit.bankAccountID = Globals.organization.defaultBankID;
    $ctrl.searchFilters = {};
    $ctrl.searchResults = [];

    $ctrl.loadDeposit(deposit);
  };
}

/*******************************************************************************
 * controller.drop */
/**
 * @ngdoc    method
 * @name     drop
 * @methodof csm.books.MakeDeposit.controller:controller
 * @description
 * Drops the current deposit.
 *******************************************************************************/
/* @ngInject */ function drop($ctrl, Alert, BusyScreen, DepositsService) {
  return function () {
    Alert.confirm(
      'This action cannot be undone. Are you sure you want to remove this deposit?',
      'Are You Sure?'
    ).then(function (resp) {
      BusyScreen.promise(
        DepositsService.dropDeposit($ctrl.depositInfo.id)
          .then(() => {
            /*------------------------------------------*/
            /* The deposit has been removed. Clear the  */
            /* current input and return to a new state. */
            /*------------------------------------------*/
            $ctrl.clear();
          })
          .catch((r) => {
            (BusyScreen.setBusy(false),
              Alert.error(
                r.parsedResponse.message(),
                'Failed to Drop Deposit',
                true
              ));
          })
      );
    });
  };
}

/*******************************************************************************
 * controller.handwritten */
/**
 * @ngdoc    method
 * @name     handwritten
 * @methodof csm.books.MakeDeposit.controller:controller
 * @param    {Boolean}  value  Value of the handwritten checkbox.
 * @description
 * This getter/setter method is used to set the handwritten flag on the deposit
 * appropriately, based off of the defaultHandwritten state from the server.
 * This is needed because different products will display the checkbox as
 * opposite meanings ("Handwritten" or "Printed?"), and we must maintain a single
 * meaning on the deposit object ("handwritten").
 *******************************************************************************/
/* @ngInject */ function handwritten($ctrl, SERVER) {
  return function (value) {
    if (!$ctrl.depositInfo) return;

    /*------------------------------------------*/
    /* Saves the handwritten value based on the */
    /* value of the defaultHandwritten flag.    */
    /*------------------------------------------*/
    if (arguments.length == 0)
      return SERVER.BooksWebConstants.defaultHandwritten
        ? !$ctrl.depositInfo.handwritten
        : $ctrl.depositInfo.handwritten;

    /*---------------------------------------*/
    /* When the defaultHandwritten flag is   */
    /* true, take the opposite of the value. */
    /*---------------------------------------*/
    $ctrl.depositInfo.handwritten = SERVER.BooksWebConstants.defaultHandwritten
      ? !value
      : value;
  };
}

/*******************************************************************************
 * controller.loadDeposit */
/**
 * @ngdoc    method
 * @name     loadDeposit
 * @methodof csm.books.MakeDeposit.controller:controller
 * @param    {Object}  deposit  Deposit object loaded from the server, or an
 *                              empty Data.DepositInfo object from the `clear`
 *                              method.
 * @description
 * Loads the new deposit object into the input.
 *******************************************************************************/
/* @ngInject */ function loadDeposit($ctrl, Alert, Globals, Watch) {
  return function (deposit) {
    /*-------------------------------------*/
    /* Deregister the watcher while we     */
    /* manipulate the deposit information. */
    /*-------------------------------------*/
    Watch.deregister();

    /*---------------------------------------------------------------*/
    /* Set the depositInfo object, and initialize the input amounts. */
    /*---------------------------------------------------------------*/
    $ctrl.depositInfo = deposit;
    $ctrl.depositDiff = 0;

    /*------------------------------------------------*/
    /* Clear the validators and any lingering alerts. */
    /*------------------------------------------------*/
    Alert.clear();
    $ctrl.validator.reset();

    /*-----------------------------------------------*/
    /* If this is a new deposit and the organization */
    /* has a default bank account selected, set the  */
    /* bank account for the deposit to this default. */
    /*-----------------------------------------------*/
    if ($ctrl.depositInfo.id == 0 && Globals.organization.defaultBankID)
      $ctrl.depositInfo.bankAccountID = Globals.organization.defaultBankID;

    /*-----------------------*/
    /* Start watching again. */
    /*-----------------------*/
    Watch.register();
  };
}

/*******************************************************************************
 * controller.print */
/**
 * @ngdoc    method
 * @name     print
 * @methodof csm.books.MakeDeposit.controller:controller
 * @param    {Number}   id    Deposit ID to print.
 * @param    {Boolean}  copy  We are printing a copy of the deposit.
 * @description
 * Prints the current deposit. If the user has confirmed that the deposit slip
 * printed correctly, the deposit will be marked as printed in the database.
 *******************************************************************************/
/* @ngInject */ function print(
  $ctrl,
  Alert,
  BusyScreen,
  DepositsService,
  Session
) {
  var PRINT_MESSAGE =
    'This Deposit Slip has been created in a Separate Window as a PDF. ' +
    'Please place a Deposit Slip Form in Your Printer and Print. -- NOTE -- ' +
    'In the Print dialog, please make sure "Page Scaling" is set to "None".';

  return function (id, copy) {
    /*-----------------------------------------------------------*/
    /* The copy parameter is listed first to get around an odd   */
    /* IE behavior. If not, the string '&copy' will appear in    */
    /* the URL which IE will encode as a copyright symbol before */
    /* sending to the server, which will break the query string. */
    /*-----------------------------------------------------------*/
    window.open(
      Session.getSessionURL(
        '/PrintDepositSlipPDF.aspx?copy={0}&slipID={1}'.format(
          copy ? 'Y' : 'N',
          id
        )
      )
    );

    /*------------------------------------*/
    /* Explain the print to the user. Ask */
    /* for confirmation if not a copy.    */
    /*------------------------------------*/
    function confirm() {
      BusyScreen.promise(
        DepositsService.confirmDeposit(id).then(function () {
          $ctrl.clear();
        })
      );
    }

    /*-------------------------------------------------------------*/
    /* If print is not confirmed, reload the deposit in view mode. */
    /*-------------------------------------------------------------*/
    function unconfirmed() {
      $ctrl.clear();
      $ctrl.viewDeposit($ctrl.depositInfo.id, 0);
    }

    /*-----------------------------------------------------------*/
    /* If this has not been printed yet, then we need to         */
    /* confirm with the user that it has been printed correctly. */
    /*-----------------------------------------------------------*/
    if (copy)
      Alert.show({
        title: 'Print Deposit Slip',
        text: PRINT_MESSAGE + 'Click Ok when finished printing',
        type: Alert.TYPE_INFO
      }).then(confirm, unconfirmed);
    else
      Alert.confirm(
        PRINT_MESSAGE + 'Did your Deposit Slip print correctly?',
        'Print Deposit Slip'
      ).then(confirm, unconfirmed);
  };
}

/*******************************************************************************
 * controller.save */
/**
 * @ngdoc    method
 * @name     save
 * @methodof csm.books.MakeDeposit.controller:controller
 * @description
 * Validates all required fields have been set, and saves the deposit.
 *******************************************************************************/
/* @ngInject */ function save($ctrl, Alert, BusyScreen, DepositsService) {
  return function () {
    Alert.clear();
    var year = $ctrl.depositInfo.entryDate
      ? $ctrl.depositInfo.entryDate.getFullYear()
      : 0;

    if (year < 1753 || year > 9999)
      return Alert.error('Deposit date is not a valid date', 'Invalid Date');

    /*------------------------------------------------*/
    /* This deposit is already saved. Allow to print. */
    /*------------------------------------------------*/
    if ($ctrl.depositInfo.id && !$ctrl.depositInfo.modified) {
      var copy = $ctrl.depositInfo.printed || $ctrl.depositInfo.handwritten;

      Alert.confirm(
        'Do you want to Print ' +
          (copy ? 'a Copy of ' : '') +
          'this Deposit Slip?',
        'Print Deposit Slip' + (copy ? ' Copy' : '')
      ).then(function (resp) {
        $ctrl.print($ctrl.depositInfo.id, copy);
      });
    } else if (!$ctrl.validate()) return;
    /*---------------------*/
    /* Validate the input. */
    /*---------------------*/
    /*-------------------*/
    /* Save the deposit. */
    /*-------------------*/ else {
      var hw = $ctrl.depositInfo.handwritten;

      Alert.confirm(
        'This will Save{0} this Deposit{1}. Do you want to continue?'.format(
          hw ? '' : ' and Print',
          hw ? '' : ' Slip'
        ),
        'Are You Sure?'
      ).then(function () {
        /*----------------------------------------*/
        /* Set default values if not set already. */
        /*----------------------------------------*/
        $ctrl.depositInfo.adjustAccountID =
          $ctrl.depositInfo.adjustAccountID || 0;

        $ctrl.depositInfo.totalAmountChecks = $ctrl.itemList.totalChecks;
        $ctrl.depositInfo.totalAmountCash = $ctrl.itemList.totalCash;

        /*-------------------*/
        /* Save the deposit. */
        /*-------------------*/
        BusyScreen.promise(
          DepositsService.saveDeposit($ctrl.depositInfo).then(
            function (id) {
              $ctrl.depositInfo.id = id;

              /*-------------------------------------*/
              /* If hadndwritten, then clear the     */
              /* input and initialize a new deposit. */
              /*-------------------------------------*/
              if ($ctrl.depositInfo.handwritten) $ctrl.clear();
              /*------------------------------------------*/
              /* Otherwise, attempt to print the deposit. */
              /*------------------------------------------*/ else
                $ctrl.print($ctrl.depositInfo.id, false);

              Alert.success('Deposit saved');
            },
            function (err) {
              Alert.error(
                err.parsedResponse.message(),
                'Could Not Save Deposit'
              );
            }
          )
        );
      });
    }
  };
}

/*******************************************************************************
 * controller.saveLabel */
/**
 * @ngdoc    method
 * @name     saveLabel
 * @methodof csm.books.MakeDeposit.controller:controller
 * @description
 * Returns a string to be used as the label for the Save/Print button.
 *******************************************************************************/
/* @ngInject */ function saveLabel($ctrl) {
  return function () {
    return $ctrl.depositInfo.isNew || $ctrl.depositInfo.modified
      ? 'Save {0}'.format(
          $ctrl.depositInfo.handwritten ? 'Deposit' : 'and Print'
        )
      : $ctrl.depositInfo.printed
        ? 'Print Copy'
        : 'Print';
  };
}

/*******************************************************************************
 * controller.setModified */
/**
 * @ngdoc    method
 * @name     setModified
 * @methodof csm.books.MakeDeposit.controller:controller
 * @param    {Boolean}  modified  Indication that the deposit has been modified.
 * @description
 * Set the modified property on the $scope.adjustment. If mDepositWatcher has not
 * been instantiated, then there is no watching being done, so the modified
 * property will not be updated.
 *******************************************************************************/
/* @ngInject */ function setModified($ctrl, $scope, Watch) {
  return function (modified) {
    if (!Watch) return;

    $ctrl.depositInfo.modified = !!modified;
  };
}

/*******************************************************************************
 * controller.updateActuals */
/**
 * @ngdoc     method
 * @name      updateActuals
 * @methodof  csm.books.MakeDeposit.controller:controller
 * @description
 *
 *******************************************************************************/
/* @ngInject */ function updateActuals($ctrl, $scope, Tools) {

  $scope.$watch('$ctrl.itemList && $ctrl.itemList.totalDeposit', function () {
    if ($ctrl.itemList) $ctrl.updateActuals();
  });

  return function () {
    var totals = {
      checksCount: 0,
      totalChecks: 0,
      totalCash: 0,
      totalAdjust: 0,
      totalDeposit: 0
    };
    $ctrl.depositInfo.items.forEach(function (item) {
      item.payments.forEach(function (payment) {
        if (payment.isCash) {
          totals.totalCash += payment.amount;
        } else if (!payment.isAdjustment) {
          totals.checksCount++;
          totals.totalChecks += payment.amount;
        }
      });
    });
    $ctrl.updateTotals();

    if ($ctrl.depositInfo.isNew) {
      $ctrl.depositInfo.totalNumberChecks = Tools.roundMoney(
        totals.checksCount
      );
      $ctrl.depositInfo.totalDepositChecks = Tools.roundMoney(
        totals.totalChecks
      );
      $ctrl.depositInfo.totalDepositCash = Tools.roundMoney(totals.totalCash);
    }
  };
}

/*******************************************************************************
 * controller.updateTotals */
/**
 * @ngdoc     method
 * @name      updateTotals
 * @methodOf  csm.books.MakeDeposit.makeDeposit.controller:controller
 * @description
 * Updates total/difference fields after deposit total or actual amounts change.
 * Adds a watch to `itemList.totalDeposit`.
 *******************************************************************************/
/* @ngInject */ function updateTotals($ctrl, $scope, Tools) {

  $scope.$watch('$ctrl.itemList && $ctrl.itemList.totalDeposit', function () {
    if ($ctrl.itemList) $ctrl.updateTotals();
  });

  return function () {
    $ctrl.depositInfo.totalAmount = Tools.roundMoney(
      $ctrl.depositInfo.totalDepositChecks + $ctrl.depositInfo.totalDepositCash
    );
    if ($ctrl.itemList && typeof $ctrl.itemList.totalDeposit !== 'undefined') {
      $ctrl.depositDiff = Tools.roundMoney(
        $ctrl.depositInfo.totalAmount - $ctrl.itemList.totalDeposit
      );
    } else {
      $ctrl.depositDiff = Tools.roundMoney($ctrl.depositInfo.totalAmount);
    }
  };
}

/*******************************************************************************
 * controller.validate */
/**
 * @ngdoc    method
 * @name     validate
 * @methodof csm.books.MakeDeposit.controller:controller
 * @description
 * Validates the input fields and properties have been set.
 *******************************************************************************/
/* @ngInject */ function validate($ctrl, Alert) {
  return function () {
    $ctrl.validator.reset();
    if (!$ctrl.validator.validate()) return false;

    /*-----------------------------------------------*/
    /* Check that the deposit has selected receipts. */
    /*-----------------------------------------------*/
    if (!$ctrl.depositInfo.items.length) {
      Alert.error('Please select items to Deposit.', 'No Items Selected');
      return false;
    }

    /*-------------------------------------------*/
    /* Ensure that an adjustment account is set. */
    /*-------------------------------------------*/
    if ($ctrl.depositDiff && !$ctrl.depositInfo.adjustAccountID) {
      Alert.error(
        'Please enter an adjustment account',
        'Adjustment Account Missing'
      );
      return false;
    }

    return true;
  };
}

/*******************************************************************************
 * controller.viewDeposit */
/**
 * @ngdoc    method
 * @name     viewDeposit
 * @methodof csm.books.MakeDeposit.controller:controller
 * @param    {Number}  id   Deposit ID.
 * @param    {Number}  dir  Direction indication. -1=Previous, 1=Next, 0=Direct
 * @description
 * Gets the deposit from the ID. If `dir` is Direct, the deposit information for
 * the `id` will be retrieved direction. For Next and Previous directions, we
 * first check for any searchResults. If any exist, we will use `mCurrentIdx` to
 * traverse that list, based on the direction requested.
 *
 * If there are no searchResults, then we will send the `id` and `dir` to the
 * server to get the next or previous receipt, in relation to the `id`.
 *******************************************************************************/
/* @ngInject */ function viewDeposit(
  $ctrl,
  Alert,
  BusyScreen,
  DepositsService,
  Data
) {
  var mCurrentIdx = 0;
  return function (id, dir) {
    /*------------------------------------*/
    /* Check for any modifications before */
    /* attempting to get the deposit.     */
    /*------------------------------------*/
    $ctrl.checkModifications(function () {
      /*--------------------------------------------------*/
      /* We will traverse the searchResults if any exist, */
      /* and we are not looking for a deposit Directly.   */
      /*--------------------------------------------------*/
      var useList = $ctrl.searchResults && $ctrl.searchResults.length > 1;

      /*-------------------------------------------------------*/
      /* Traverse the saved search list to reset the id to the */
      /* next or previous deposit, depending on the direction. */
      /* Then clear the direction to load that specific id.    */
      /*-------------------------------------------------------*/
      if (useList) {
        /*----------------------------------------------------------------*/
        /* If we are trying to load a deposit directly, find the index of */
        /* the deposit from the searchResults, and set it as mCurrentIdx. */
        /*----------------------------------------------------------------*/
        if (!dir) {
          for (var i = 0; i < $ctrl.searchResults.length; i++)
            if ($ctrl.searchResults[i].id == id) {
              mCurrentIdx = i;
              break;
            }
        } else {
          /*--------------------------------------------------*/
          /* Otherwise, reset the `id` from the searchResults */
          /* based on the mCurrentIdx, and clear the `dir`.   */
          /*--------------------------------------------------*/
          /*-------------------------------------------------------------------*/
          /* We need to take the opposite direction because the searchResults  */
          /* are sorted by date in descending order, and going to the previous */
          /* deposit will require going to the next element in the list.       */
          /*-------------------------------------------------------------------*/
          var idx = mCurrentIdx - dir;

          /*----------------------------------------------------------------*/
          /* If we are about to go outside the bounds of the array, alert   */
          /* the user that they have reached the end of the search results. */
          /*----------------------------------------------------------------*/
          if (idx < 0 || idx >= $ctrl.searchResults.length) {
            Alert.info('There are no more deposits', 'No More Deposits');
            return;
          }

          /*--------------------------------------------------------*/
          /* Set the new curreni index. Reset `id` to the traversed */
          /* deposit's id, and reset the `dir` to be Direct.        */
          /*--------------------------------------------------------*/
          mCurrentIdx = idx;
          id = $ctrl.searchResults[mCurrentIdx].id;
          dir = 0;
        }
      }

      /*------------------------------------------------------*/
      /* Get the next or previous deposit from the current    */
      /* `id`. If `dir` is zero, then load the specific `id`. */
      /*------------------------------------------------------*/
      BusyScreen.promise(
        DepositsService.getDeposit(id || 0, dir || 0).then(
          function (data) {
            /*-------------------------------*/
            /* Load the deposit into the ui. */
            /*-------------------------------*/
            if (data) {
              var deposit = new Data.DepositInfo(data);

              /*-----------------------------------------*/
              /* Load the deposit if it can be reviewed, */
              /* otherwise perform the requested action. */
              /*-----------------------------------------*/
              if (!deposit.canReview) {
                if (!dir)
                  Alert.info(
                    'This deposit cannot be reviewed in this app',
                    'Reports Only'
                  );
                else $ctrl.viewDeposit(deposit.id, dir);

                return;
              }

              $ctrl.loadDeposit(deposit);
            } else {
              /*----------------------------------------------------------------*/
              /* No deposit was found. If this was for the next direction, load */
              /* a new deposit, otherwise show the no deposits found message.   */
              /*----------------------------------------------------------------*/
              if (dir > 0) $ctrl.checkModifications($ctrl.clear);
              else
                Alert.info(
                  'There are no previous deposits.',
                  'No More Deposits'
                );
            }
          },
          /*---------------------------------------------------------------*/
          /* The server attempted to retrieve a deposit that didn't exist. */
          /*---------------------------------------------------------------*/
          function (err) {
            Alert.info('There are no more deposits.', 'No More Deposits');
          }
        )
      );
    });
  };
}
